import React, { useState, useEffect, useCallback } from 'react';
import '../../styles/components/FormPage.css';
import '../../styles/components/ProjectTimeline.css';
import { Input as FormInput, Select as FormSelect, DateTimeInput as FormDateTimeInput } from '../FormField';
import RichTextEditor from '../ui/RichTextEditor';
import { CalendarEvent } from '../../types/Planner';
import { loadCalendarEvents, createCalendarEventStrict, updateCalendarEventStrict } from '../../services/CalendarService';
import { useFormSaveEvent, firstMissingField, useClearErrorsOnEdit } from './_shared';
import { useNotifications } from '../NotificationProvider';

const CALENDAR_CATEGORY_OPTIONS: { value: CalendarEvent['category']; label: string }[] = [
  { value: 'ceremony', label: 'Ceremony' }, { value: 'reception', label: 'Reception' },
  { value: 'cocktail', label: 'Cocktail Hour' }, { value: 'afterparty', label: 'After Party' },
  { value: 'planning', label: 'Planning' }, { value: 'vendor', label: 'Vendor Meeting' },
  { value: 'rehearsal', label: 'Rehearsal' }, { value: 'official', label: 'Official' },
  { value: 'social', label: 'Social' },
];

const pad2 = (n: number) => n.toString().padStart(2, '0');
const isoDateStr = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

interface CalendarEventFormPageProps {
  eventId?: string;
  prefillDate?: string;
  prefillStartTime?: string;
  onSave: () => void;
  onCancel: () => void;
}

export const CalendarEventFormPage: React.FC<CalendarEventFormPageProps> = ({
  eventId, prefillDate, prefillStartTime, onSave, onCancel,
}) => {
  const { showError } = useNotifications();
  const startH = prefillStartTime ? parseInt(prefillStartTime.split(':')[0], 10) : 9;
  const endH = Math.min(startH + 1, 23);

  const [form, setForm] = useState({
    title: '',
    category: 'planning' as CalendarEvent['category'],
    date: prefillDate || isoDateStr(new Date()),
    startTime: prefillStartTime || '09:00',
    endTime: `${pad2(endH)}:00`,
    location: '',
    description: '',
  });

  useEffect(() => {
    if (!eventId) return;
    const existing = loadCalendarEvents().find(e => e.id === eventId);
    if (!existing) return;
    const startDate = new Date(existing.start);
    const endDate = new Date(existing.end);
    setForm({
      title: existing.title,
      category: existing.category,
      date: isoDateStr(startDate),
      startTime: `${pad2(startDate.getHours())}:${pad2(startDate.getMinutes())}`,
      endTime: `${pad2(endDate.getHours())}:${pad2(endDate.getMinutes())}`,
      location: existing.location || '',
      description: existing.description || '',
    });
  }, [eventId]);

  const handleSave = useCallback(async () => {
    const missing = firstMissingField([{ label: 'Title', value: form.title }]);
    if (missing) { showError(missing); return; }
    const fields = {
      title: form.title.trim(),
      start: `${form.date}T${form.startTime}:00`,
      end: `${form.date}T${form.endTime}:00`,
      category: form.category,
      type: form.category,
      location: form.location,
      description: form.description,
    };
    // Both calls wait for the API and throw on failure, so a 422 reaches the
    // shared hook (field errors under their fields) and the form stays open.
    if (eventId) {
      await updateCalendarEventStrict(eventId, fields);
    } else {
      await createCalendarEventStrict(fields);
    }
    onSave();
  }, [eventId, form, onSave, showError]);

  const fieldErrors = useFormSaveEvent(handleSave, { fallback: 'Failed to save the event.' });
  useClearErrorsOnEdit(form, fieldErrors, {
    date: 'start_time', startTime: 'start_time', endTime: 'end_time', description: 'description',
  });

  return (
    <div className="inline-form-page">
      <FormInput label="Title" type="text" placeholder="Task title" value={form.title}
        error={fieldErrors.errorFor('title')}
        onChange={e => setForm({ ...form, title: e.target.value })} autoFocus required />
      <div className="form-row">
        <FormSelect label="Category" value={form.category} error={fieldErrors.errorFor('category')}
          onChange={e => setForm({ ...form, category: e.target.value as CalendarEvent['category'] })}
          options={CALENDAR_CATEGORY_OPTIONS.map(c => ({ value: c.value, label: c.label }))} />
        <FormDateTimeInput label="Starts" date={form.date} time={form.startTime}
          error={fieldErrors.errorFor('start_time')}
          onChange={(date, startTime) => setForm({ ...form, date, startTime })} />
      </div>
      {/* "Ends" stays a bare time, not a second date-and-time: a calendar
          event here carries ONE date, so it cannot cross midnight and an end
          date would be a field that can only ever repeat the start. */}
      <FormInput label="Ends" type="time" value={form.endTime}
        error={fieldErrors.errorFor('end_time')}
        onChange={e => setForm({ ...form, endTime: e.target.value })}
        help="Same day as the start" />
      <FormInput label="Location" type="text" placeholder="Venue or address" value={form.location}
        error={fieldErrors.errorFor('location')}
        onChange={e => setForm({ ...form, location: e.target.value })} />
      <RichTextEditor label="Notes" placeholder="Optional notes..." value={form.description}
        error={fieldErrors.errorFor('description')}
        onChange={val => setForm({ ...form, description: val })} rows={3} />
    </div>
  );
};
